import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import {
  getDemoRequestsRequest,
  updateDemoRequestRequest,
  deleteDemoRequestRequest,
} from "../../../store/DemoRequests/actions";
import messages from "../../../assets/locale/messages";
import { textContainOnlySpaces } from "../../../utils/Helpers";
import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import { MonitorPlay, TriangleAlert } from "lucide-react";

import DemoRequestsTable from "../../../components/DemoRequests/DemoRequestsTable";
import { UICallout, UIEmptyState, UIPaginationBar } from "../../../components/ui";
import "./DemoRequests.scss";

const API_DATE_FORMAT = "YYYY-MM-DD";

const DemoRequests = () => {
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const { demoRequests, shared } = messages[lang];
  const labels = demoRequests.listDemoRequests;

  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.demoRequests.list);
  const meta = useSelector((state) => state.demoRequests.meta);

  const [searchValue, setSearchValue] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [rowsCount, setRowsCount] = useState(10);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const buildParams = (pageNo, search, from, to) => ({
    page: pageNo,
    items: rowsCount,
    company_name: search || undefined,
    from_date: from ? dayjs(from).format(API_DATE_FORMAT) : undefined,
    to_date: to ? dayjs(to).format(API_DATE_FORMAT) : undefined,
    order_by_created_at: "desc",
  });

  const getDemoRequests = (pageNo, search, from, to) => {
    dispatch(getDemoRequestsRequest(buildParams(pageNo, search, from, to)));
  };

  useEffect(() => {
    getDemoRequests(page, searchValue, fromDate, toDate);
  }, [lang]);

  useEffect(() => {
    getDemoRequests(page, searchValue, fromDate, toDate);
  }, [page, rowsCount]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getDemoRequests(1, searchValue, fromDate, toDate);
    }
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
    getDemoRequests(1, searchValue, fromDate, toDate);
  }, [fromDate, toDate]);

  const handleRowsCountChange = (value) => {
    setRowsCount(value);
    setPage(1);
  };

  const handleToggleRow = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  const handleToggleContacted = (rowId, status) => {
    dispatch(
      updateDemoRequestRequest({
        id: rowId,
        data: { status },
        listParams: buildParams(page, searchValue, fromDate, toDate),
      }),
    );
  };

  const handleDelete = (rowId, name) => {
    dispatch(
      deleteDemoRequestRequest({
        id: rowId,
        name,
        listParams: buildParams(page, searchValue, fromDate, toDate),
      }),
    );
  };

  const paginationTotal = meta?.count || 0;
  const paginationCurrentPage = meta?.currentPage || page || 1;
  const paginationFrom =
    paginationTotal === 0 ? 0 : (paginationCurrentPage - 1) * rowsCount + 1;
  const paginationTo = Math.min(
    paginationCurrentPage * rowsCount,
    paginationTotal,
  );
  const paginationPages =
    meta?.pages || Math.ceil(paginationTotal / rowsCount) || 1;

  const tableLabels = useMemo(
    () => ({
      details: labels.details,
      name: labels.name,
      workEmail: labels.workEmail,
      phoneNumber: labels.phoneNumber,
      companyName: labels.companyName,
      dateRequested: labels.dateRequested,
      contacted: labels.contacted,
      actions: labels.actions,
      delete: labels.delete,
      remove: labels.remove,
      removeTitle: labels.removeTitle,
      removeDescription: labels.removeDescription,
      removeAction: labels.removeAction,
      cancel: shared.cancel,
      expand: labels.expand,
      collapse: labels.collapse,
      requestDetailsLabel: labels.requestDetailsLabel,
    }),
    [labels],
  );

  const renderContent = () => {
    if (!isLoading && !list?.length) {
      return (
          <UIEmptyState
            title={labels.noDataDescription}
            icon={<MonitorPlay />}
            classNames={{ root: "py-5" }}
          />
      );
    }

    return (
      <>
        <DemoRequestsTable
          rows={list}
          labels={tableLabels}
          expandedRowId={expandedRowId}
          onToggleRow={handleToggleRow}
          onToggleContacted={handleToggleContacted}
          onDelete={handleDelete}
        />
          <UICallout
            variant="warning"
            icon={<TriangleAlert />}
            description={
              <>
                <span className="demo-requests-container__banner-label">
                  {labels.bannerNoteLabel}
                </span>{" "}
                {labels.bannerNote}
              </>
            }
            className="demo-requests-container__banner"
          />
        {paginationTotal > 0 && (
          <UIPaginationBar
            currentPage={paginationCurrentPage}
            pages={paginationPages}
            pageSize={rowsCount}
            rangeTemplate={labels.showingRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={paginationTotal}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={(nextPage) => setPage(nextPage)}
            onPageSizeChange={handleRowsCountChange}
            className="demo-requests-container__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="conatiner demo-requests-container">
      <div className="my-3">
        <Breadcrumbs
          list={[{ path: "", label: labels.list }]}
          BreadCrumbHasBackIcon={false}
          className="mb-4"
        />
      </div>
      <Filters
        isCollapsable
        searchInput={{
          placeholder: labels.searchPlaceholder,
          name: "search",
          value: searchValue,
          onChange: (value) => setSearchValue(value),
        }}
        filters={[
          {
            type: "date",
            name: "fromDate",
            label: labels.fromDate,
            placeholder: labels.fromDate,
            value: fromDate,
            onChange: (value) => setFromDate(value),
          },
          {
            type: "date",
            name: "toDate",
            label: labels.toDate,
            placeholder: labels.toDate,
            value: toDate,
            onChange: (value) => setToDate(value),
          },
        ]}
        classNames={{
          container: "demo-requests-container__filters",
          base: "",
          search: "mb-3 mb-md-0",
          filter: "",
        }}
      />
      <div className="demo-requests-container__content d-flex flex-column">
        {renderContent()}
      </div>
    </div>
  );
};

export default DemoRequests;
